import { CopyOutlined, DeleteOutlined, FormOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Input, Modal, Select, Table } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AdvertiserBean, CampaignBean, ItemResponseCampaignBeanObject, ListPagination } from 'src/api/api'
import campaignApi from 'src/component/app/campaign/CampaignApi'
import { formDataLoader, FormDataType } from 'src/util/FormDataLoader'
import { StorageKey, storageReaderFactory, storageWriterFactory } from 'src/util/Storage'
import { useDebounce } from 'src/util/useDebounce'
import { toSort, useSorter } from 'src/util/useSorter'
import { capitalizedView, daysToEnd, formatLocale } from 'src/util/Util'

const Column = Table.Column
const Option = Select.Option

const storageWriter = storageWriterFactory('campaign')
const storageReader = storageReaderFactory('campaign')

const now = dayjs()

const formInfoItems: FormDataType[] = [
    FormDataType.Advertisers
]

const CampaignList = () => {

    const [data, setData] = useState<CampaignBean[]>([])
    const [pagination, setPagination] = useState<any>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1
    })

    const [info, setInfo] = useState({
        advertisers: []
    })

    const [search, setSearch] = useState<string>()
    const debouncedSearch = useDebounce(search, 500)

    const [advertiser, setAdvertiser] = useState<number>()
    const [status, setStatus] = useState<CampaignBean.StatusEnum>()
    const [error, setError] = useState()

    const loadData = async (page: number) => {
        const response = await campaignApi.list(pagination.defaultPageSize, page, toSort(sorter), search, advertiser, status)

        const array = response.data as Array<ItemResponseCampaignBeanObject>
        const campaignBeans: CampaignBean[] = array.map(item => item.data as CampaignBean)

        if (campaignBeans.length == 0 && page > 1) {
            loadData(page - 1)
        } else {
            const newPagination: ListPagination = response.pagination as ListPagination
            storageWriter.write(StorageKey.SearchParams, {
                page: newPagination.currentPage
            })

            setData(campaignBeans)
            setPagination({ ...pagination, total: newPagination.total, current: newPagination.currentPage })
        }
    }

    const { sorter, handleTableChange } = useSorter(pagination, loadData)

    const loadInfo = async () => {
        const resp = await formDataLoader(...formInfoItems)
        setInfo(resp as any)
    }

    useEffect(() => {
        loadInfo()
    }, [])

    useEffect(() => {
        const searchParams = storageReader.read(StorageKey.SearchParams)
        const page = searchParams.page || pagination.current

        loadData(page)
    }, [debouncedSearch, advertiser, status, sorter])

    const remove = (record: CampaignBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: async () => {
                try {
                    await campaignApi.delete(record.id as number)

                    let page = pagination.current
                    if (data.length == 1 && pagination.current > 1) {
                        page -= 1
                    }

                    loadData(page)
                } catch (e) {
                    setError(e.error)
                }
            }
        })
    }

    return <Card
        title="Campaign"
        className="list-card"
        extra={<Link to={`/campaign/create`} className="create-new-link">
            <Button htmlType="button" type="primary" icon={<PlusOutlined />}>
                New Campaign
            </Button>
        </Link>}
    >
        {error && <Alert message={error} type="error" />}
        <div className="list-filter-group">
            <Input prefix={<SearchOutlined />} placeholder="Search by ID, Name" onChange={e => setSearch(e.target.value || undefined)} />
            <Select placeholder="Filter by Advertiser" onChange={setAdvertiser} allowClear>
                {info.advertisers.map((item: AdvertiserBean) => <Option key={item.id} value={item.id}>{item.name}</Option>)}
            </Select>
            <Select placeholder="Filter by Status" onChange={setStatus} allowClear>
                {Object.values(CampaignBean.StatusEnum).map(type => <Option key={type} value={type}>{capitalizedView(type)}</Option>)}
            </Select>
        </div>
        <Table
            rowKey="id"
            size="middle"
            dataSource={data}
            pagination={{ ...pagination, size: 'default' }}
            onChange={handleTableChange}
        >
            <Column
                sorter
                key="id"
                title="ID"
                dataIndex="id"
            />
            <Column
                sorter
                key="name"
                title="Name"
                dataIndex="name"
                render={(text, record: CampaignBean) => <Link to={`/campaign/${record.id}/edit`}>{text}</Link>}
            />
            <Column
                key="advertiserId"
                title="Advertiser"
                dataIndex="advertiserId"
                render={id => (info.advertisers as AdvertiserBean[]).find(item => id == item.id)?.name}
            />
            <Column
                key="totalBudget"
                title="Total Budget"
                dataIndex="totalBudget"
                render={amount => formatLocale(amount ?? 0, v => '$' + v)}
            />
            <Column
                key="totalCost"
                title="Total Cost"
                dataIndex="totalCost"
                render={amount => formatLocale(amount ?? 0, v => '$' + v)}
            />
            <Column
                key="daysLeft"
                title="Days Left"
                dataIndex="endDate"
                render={endDate => daysToEnd(now, endDate)}
            />
            <Column
                sorter
                key="status"
                title="Status"
                dataIndex="status"
                render={(text, record: CampaignBean) => capitalizedView(record.status)}
            />
            <Column
                key="modified"
                title="Last Modified"
                dataIndex="modified"
                sorter
            />
            <Column align="center" key="action" title="Actions" render={(text: string, record: CampaignBean) => (
                <span>
                    <Link to={`/campaign/${record.id}/edit`}>
                        <Button type="text" >
                            <FormOutlined className="actions-btn" />
                        </Button>
                    </Link>
                    <Link to={`/campaign/create?copyCampaignId=${record.id}`}>
                        <Button type="text" >
                            <CopyOutlined className="actions-btn" />
                        </Button>
                    </Link>
                    <Button type="text" danger onClick={() => remove(record)}>
                        <DeleteOutlined />
                    </Button>
                </span>
            )} />
        </Table>
    </Card>
}

export default CampaignList



// WEBPACK FOOTER //
// ./src/component/app/campaign/CampaignList.tsx